/**
 * How fast the clock runs: the ladder of rungs and what each one means.
 *
 * Here rather than in the HUD because three places need it and only one draws
 * anything: the HUD draws the buttons, `platform/input.ts` binds the digit keys
 * to the same rungs, and `main.ts` turns a rung into milliseconds. The ladder
 * is Sable Reserve's.
 */

/** A rung, as a multiplier on `DAY_MS_AT_1X`. Zero is paused. */
export type SpeedSetting = 0 | 0.25 | 0.5 | 1 | 4 | 16 | 32 | 64 | 128;

export const PAUSED = 0;

export const SPEEDS: readonly SpeedSetting[] = [0, 0.25, 0.5, 1, 4, 16, 32, 64, 128];

/**
 * How long one simulated day lasts at 1x, in milliseconds.
 *
 * DECISION, the user's (M92): five seconds, as Sable Reserve's, so a year of
 * 365 days takes 30 minutes 25 seconds at normal speed. The design brief had
 * four (24 minutes 20 seconds), within Sable Reserve's reason for not going
 * much faster: the renderer's interpolation must read as movement, not a
 * flicker.
 */
export const DAY_MS_AT_1X = 5000;

/** Real seconds per simulated day at this rung. */
export function secondsPerDay(speed: SpeedSetting): number {
  return speed === PAUSED ? Infinity : DAY_MS_AT_1X / 1000 / speed;
}

/** "Pause", "¼×", "1×", "128×". Single glyphs for the fractions, so nine buttons fit. */
export function speedLabel(speed: SpeedSetting): string {
  if (speed === PAUSED) return 'Pause';
  if (speed === 0.25) return '¼×';
  if (speed === 0.5) return '½×';
  return `${speed}×`;
}
